import React from 'react';

import {
    AmexIcon,
    CreditCardIcon,
    MastercardIcon,
    VisaIcon
} from '../components/common';
import {
    getPaymentMethodConfig,
    getPaymentMethodIcon
} from '../constants/payment-method-config';
import {
    PaymentMethod,
    PaymentMethodBrand,
    SavedPaymentMethod
} from '../types';

// ============================================================================
// PAYMENT METHOD CLASSIFICATION
// ============================================================================

/**
 * Payment methods that use external provider widgets (Adyen, Stripe, etc.)
 */
export const PROVIDER_BASED_METHODS: PaymentMethod[] = [
    'CREDIT_CARD',
    'PAYPAL',
    'BANCONTACT',
    'IDEAL',
    'TWINT',
    'BACS',
    'BECS',
    'ACH'
];

/**
 * Payment methods that use internal React forms
 */
export const FORM_BASED_METHODS: PaymentMethod[] = ['SEPA', 'CH_DD', 'LSV'];

/**
 * Payment methods that are processed directly without forms
 */
export const DIRECT_PAYMENT_METHODS: PaymentMethod[] = [
    'CASH',
    'BANK_TRANSFER'
];

export const REDIRECT_BASED_METHODS: PaymentMethod[] = [
    'PAYPAL',
    'IDEAL',
    'TWINT'
];

/**
 * Determines if a payment method uses external provider widgets
 */
export const isProviderBasedMethod = (method: PaymentMethod): boolean => {
    return PROVIDER_BASED_METHODS.includes(method);
};

/**
 * Determines if a payment method uses internal React forms
 */
export const isFormBasedMethod = (method: PaymentMethod): boolean => {
    return FORM_BASED_METHODS.includes(method);
};

/**
 * Determines if a payment method is processed directly without forms
 */
export const isDirectPaymentMethod = (method: PaymentMethod): boolean => {
    return DIRECT_PAYMENT_METHODS.includes(method);
};

/**
 * Determines if a payment method requires redirect and doesn't support programmatic submission
 */
export const isRedirectBasedMethod = (method: PaymentMethod): boolean => {
    return REDIRECT_BASED_METHODS.includes(method);
};

// ============================================================================
// PAYMENT METHOD NAMES & DESCRIPTIONS
// ============================================================================

export const getPaymentMethodTypeName = (
    methodType: PaymentMethod,
    t: (key: string, fallback?: string) => string
): string => {
    switch (methodType) {
        case 'CREDIT_CARD':
            return t('upc.credit.card', 'Credit Card');
        case 'PAYPAL':
            return t('upc.paypal', 'PayPal');
        case 'BANCONTACT':
            return t('upc.bancontact', 'Bancontact');
        case 'IDEAL':
            return t('upc.ideal', 'iDEAL');
        case 'BACS':
            return t('upc.bacs', 'BACS Direct Debit');
        case 'BECS':
            return t('upc.becs', 'BECS Direct Debit');
        case 'TWINT':
            return t('upc.twint', 'TWINT');
        case 'SEPA':
            return t('upc.sepa', 'SEPA');
        case 'CH_DD':
            return t('upc.ch.dd', 'Swiss Direct Debit');
        case 'LSV':
            return t('upc.lsv', 'LSV');
        case 'CASH':
            return t('upc.cash', 'Cash');
        case 'BANK_TRANSFER':
            return t('upc.bank.transfer', 'Bank Transfer');
        case 'ACH':
            return t('upc.ach', 'ACH Direct Debit');
        default:
            return t('upc.payment.method', 'Payment Method');
    }
};

export const getPaymentMethodTypeDescription = (
    methodType: PaymentMethod,
    t: (key: string, fallback?: string) => string
): string => {
    switch (methodType) {
        case 'CREDIT_CARD':
            return t(
                'upc.credit.card.description',
                'Pay with Visa, Mastercard, or other credit cards'
            );
        case 'PAYPAL':
            return t(
                'upc.paypal.description',
                'Pay securely with your PayPal account'
            );
        case 'BANCONTACT':
            return t('upc.bancontact.description', 'Belgian payment method');
        case 'IDEAL':
            return t('upc.ideal.description', 'Dutch bank transfer');
        case 'BACS':
            return t('upc.bacs.description', 'UK Direct Debit');
        case 'BECS':
            return t('upc.becs.description', 'Australian BECS Direct Debit');
        case 'TWINT':
            return t('upc.twint.description', 'Swiss mobile payment');
        case 'SEPA':
            return t('upc.sepa.description', 'Single Euro Payments Area');
        case 'CH_DD':
            return t('upc.ch.dd.description', 'Swiss direct debit payment');
        case 'LSV':
            return t('upc.lsv.description', 'LSV direct debit payment');
        case 'CASH':
            return t('upc.cash.description', 'Pay with cash');
        case 'BANK_TRANSFER':
            return t('upc.bank.transfer.description', 'Pay via bank transfer');
        case 'ACH':
            return t(
                'upc.ach.description',
                'US Automated Clearing House Direct Debit'
            );
        default:
            return t(
                'upc.payment.method.description',
                'Unknown payment method'
            );
    }
};

// ============================================================================
// BRAND UTILITIES
// ============================================================================

export const getBrandName = (brand?: PaymentMethodBrand): string => {
    switch (brand) {
        case 'VISA':
            return 'Visa';
        case 'MASTER_CARD':
            return 'Mastercard';
        case 'AMERICAN_EXPRESS':
            return 'American Express';
        case 'DISCOVER':
            return 'Discover';
        case 'JCB':
            return 'JCB';
        case 'DINERS':
            return 'Diners Club';
        case 'MAESTRO':
            return 'Maestro';
        case 'V_PAY':
            return 'V PAY';
        case 'BAN_CONTACT':
            return 'Bancontact';
        case 'CARTES_BANCAIRES':
            return 'Cartes Bancaires';
        case 'CUP':
            return 'China UnionPay';
        case 'ELO':
            return 'Elo';
        case 'GIROCARD':
            return 'Girocard';
        case 'HIPERCARD':
            return 'Hipercard';
        case 'TWINT_POS':
            return 'TWINT';
        case 'UNKNOWN':
        default:
            return 'Card';
    }
};

export const getBrandIcon = (brand?: PaymentMethodBrand): React.ReactNode => {
    switch (brand) {
        case 'VISA':
            return <VisaIcon />;
        case 'MASTER_CARD':
            return <MastercardIcon />;
        case 'AMERICAN_EXPRESS':
            return <AmexIcon />;
        case 'MAESTRO':
        case 'V_PAY':
        case 'DISCOVER':
        case 'JCB':
        case 'DINERS':
        case 'BAN_CONTACT':
        case 'CARTES_BANCAIRES':
        case 'CUP':
        case 'ELO':
        case 'GIROCARD':
        case 'HIPERCARD':
        case 'TWINT_POS':
        case 'UNKNOWN':
        default:
            return <CreditCardIcon />;
    }
};

// ============================================================================
// DISPLAY INFO GENERATION
// ============================================================================

export interface PaymentMethodDisplayInfo {
    title: string;
    subtitle?: string;
    icon: React.ReactNode;
    isDefault?: boolean;
}

export const getPaymentMethodDisplayInfo = (
    method: PaymentMethod,
    t?: (key: string, fallback?: string) => string
): PaymentMethodDisplayInfo => {
    const config = getPaymentMethodConfig(method);
    const name = t ? getPaymentMethodTypeName(method, t) : config.name;

    return {
        title: name,
        subtitle: name,
        icon: getPaymentMethodIcon(method)
    };
};

export const getSavedPaymentMethodDisplayInfo = (
    savedMethod: SavedPaymentMethod,
    t: (key: string, fallback?: string) => string
): PaymentMethodDisplayInfo => {
    const baseInfo = getPaymentMethodDisplayInfo(savedMethod.paymentMethod, t);
    const { title, subtitle } = generateSavedMethodText(savedMethod, t);

    return {
        ...baseInfo,
        title,
        subtitle,
        icon:
            savedMethod.paymentMethod === 'CREDIT_CARD'
                ? getBrandIcon(savedMethod.details.creditCard?.brand)
                : getPaymentMethodIcon(savedMethod.paymentMethod)
    };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getLastFourDigits = (value: string | undefined): string => {
    if (!value) {
        return '';
    }
    return value.slice(-4);
};

const formatSimpleMethodText = (
    title: string,
    subtitle: string
): { title: string; subtitle?: string } => ({
    title,
    subtitle
});

const generateSavedMethodText = (
    savedMethod: SavedPaymentMethod,
    t: (key: string, fallback?: string) => string
): { title: string; subtitle?: string } => {
    const paymentMethodName = getPaymentMethodTypeName(
        savedMethod.paymentMethod,
        t
    );
    const paymentMethodDescription = getPaymentMethodTypeDescription(
        savedMethod.paymentMethod,
        t
    );

    switch (savedMethod.paymentMethod) {
        case 'CREDIT_CARD': {
            const { creditCard } = savedMethod.details;
            const brandName = getBrandName(creditCard?.brand);
            return {
                title: `${creditCard?.cardHolder ?? brandName} •••• ${creditCard?.cardNumber || ''}`,
                subtitle: `${t('upc.credit.card.expires', 'Expires')} ${creditCard?.expiry}`
            };
        }
        case 'BANCONTACT': {
            const bancontact = savedMethod.details.banContactCard;
            return {
                title: `${bancontact?.cardHolder ?? 'Bancontact'} •••• ${bancontact?.cardNumber ?? ''}`,
                subtitle: `${t('upc.credit.card.expires', 'Expires')} ${bancontact?.expiry}`
            };
        }
        case 'BACS': {
            const { bacs } = savedMethod.details;
            return {
                title: `${bacs?.accountHolder ?? paymentMethodName} •••• ${getLastFourDigits(bacs?.bankAccountNumber)}`,
                subtitle: paymentMethodName
            };
        }
        case 'BECS': {
            const { becs } = savedMethod.details;
            return {
                title: `${becs?.accountHolder ?? paymentMethodName} •••• ${getLastFourDigits(becs?.bankAccountNumber)}`,
                subtitle: paymentMethodName
            };
        }
        case 'SEPA': {
            const { sepa } = savedMethod.details;
            return {
                title: `${sepa?.bankAccountDetails.accountHolder ?? paymentMethodName} •••• ${getLastFourDigits(sepa?.bankAccountDetails.iban)}`,
                subtitle: paymentMethodName
            };
        }
        case 'CH_DD': {
            const { chDD } = savedMethod.details;
            return {
                title: `${chDD?.bankAccountDetails.accountHolder ?? paymentMethodName} •••• ${getLastFourDigits(chDD?.bankAccountDetails.iban)}`,
                subtitle: paymentMethodName
            };
        }
        case 'LSV': {
            const { lsvDD } = savedMethod.details;
            return {
                title: `${lsvDD?.bankAccountDetails.accountHolder ?? paymentMethodName} •••• ${getLastFourDigits(lsvDD?.bankAccountDetails.iban)}`,
                subtitle: paymentMethodName
            };
        }
        case 'TWINT':
            return formatSimpleMethodText('TWINT', paymentMethodDescription);
        case 'IDEAL':
            return formatSimpleMethodText('IDEAL', paymentMethodDescription);
        case 'ACH':
            return formatSimpleMethodText('ACH', paymentMethodDescription);
        default:
            return { title: paymentMethodName };
    }
};
